// components/ImageCompressor/ImageCompressor.jsx


// 在其他地方使用这个组件，可以这样传：
// <ImageCompressor
//   onCompress={handleCompress}
//   compressionRatio={90} // 强制 90% 质量
//   showUI={false}
// >
//   <button>选照片</button>
// </ImageCompressor>

// components/ImageCompressor/ImageCompressor.jsx
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './index.module.css';

/**
 * 图片压缩组件
 * @param {Object} props
 * @param {Function} props.onCompress 压缩完成回调 (compressedFile, originalFile, compressionParams) => void
 * @param {Function} props.onError 压缩失败回调 (error) => void
 * @param {boolean} props.showUI 是否显示完整UI界面，默认 false（静默压缩）
 * @param {number} props.compressionRatio 压缩质量百分比 (10-100)。如果不传，则智能判断：
 *                                        原图 > 1MB 时默认 30%，<= 1MB 时默认 80%
 * @param {number} props.maxWidth 最大宽度（像素），默认 800
 * @param {number} props.maxHeight 最大高度（像素），默认 800
 * @param {number} props.maxSizeKB 最大文件大小（KB），默认 200
 * @param {string} props.accept 接受的文件类型，默认 'image/jpeg,image/jpg,image/png'
 * @param {React.ReactNode} props.children 自定义触发按钮（showUI=false 时生效）
 * @param {string} props.triggerText 触发按钮文字（showUI=true 时生效）
 */
const ImageCompressor = ({
  onCompress,
  onError,
  showUI = false,
  compressionRatio, // 这里不再给默认值 80，交由内部逻辑处理
  maxWidth: defaultMaxWidth = 800,
  maxHeight: defaultMaxHeight = 800,
  maxSizeKB: defaultMaxSizeKB = 200,
  accept = 'image/jpeg,image/jpg,image/png',
  children,
  triggerText = '选择图片'
}) => {
  // UI 模式的状态
  const [files, setFiles] = useState([]);
  const [compressedFiles, setCompressedFiles] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const [message, setMessage] = useState('');
  
  // UI 模式下调参状态
  const [quality, setQuality] = useState(compressionRatio || 80);
  const [maxWidth, setMaxWidth] = useState(defaultMaxWidth);
  const [maxHeight, setMaxHeight] = useState(defaultMaxHeight);
  const [maxSizeKB, setMaxSizeKB] = useState(defaultMaxSizeKB);
  
  // 预览相关
  const [previewImage, setPreviewImage] = useState(null);
  const [originalSize, setOriginalSize] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // 检查是否为移动端
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  /**
   * 压缩单张图片
   */
  const compressImage = useCallback((file, currentQuality, currentMaxWidth, currentMaxHeight, currentMaxSizeKB) => {
    return new Promise((resolve, reject) => {
      // 验证文件类型
      if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
        reject(new Error('只支持 JPG/PNG 格式的图片'));
        return;
      }

      const img = new Image();
      const imageUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(imageUrl);

        let { width, height } = img;
        
        // 计算缩放比例
        if (width > currentMaxWidth || height > currentMaxHeight) {
          const ratio = Math.min(currentMaxWidth / width, currentMaxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        // 白底（防止透明背景变黑）
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, width, height);

        // 尝试压缩，如果超过限制则降低质量
        const tryCompress = (currentQualityValue) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('压缩失败'));
                return;
              }

              // 检查压缩后大小是否满足要求（如果降到了最低质量 20% 还是大，就强制输出）
              if (blob.size <= currentMaxSizeKB * 1024 || currentQualityValue <= 20) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve({
                  blob,
                  file: compressedFile,
                  originalSize: file.size,
                  compressedSize: blob.size,
                  width,
                  height,
                  originalWidth: img.width,
                  originalHeight: img.height
                });
              } else {
                // 降低质量继续压缩
                tryCompress(currentQualityValue - 10);
              }
            },
            'image/jpeg',
            currentQualityValue / 100
          );
        };

        tryCompress(currentQuality);
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('图片加载失败'));
      };

      img.src = imageUrl;
    });
  }, []);

  /**
   * 处理文件选择（静默模式）
   */
  const handleFileSelectSilent = async (event) => {
    const file = event.target.files[0];
    // 清空 value 让同一张图能触发多次 onChange
    event.target.value = '';
    
    if (!file) return;

    try {
      // ★ 核心逻辑：智能判断压缩比例
      let finalQuality = 80; // 默认 80%
      
      if (compressionRatio) {
        // 如果外部明确指定了 compressionRatio，强制使用指定的
        finalQuality = compressionRatio;
      } else if (file.size > 1024 * 1024) {
        // 如果没有指定，且图片超过 1MB (1024 * 1024 bytes)，则使用 30%
        finalQuality = 30;
      }

      const result = await compressImage(file, finalQuality, defaultMaxWidth, defaultMaxHeight, defaultMaxSizeKB);
      onCompress?.(result.file, file, {
        quality: finalQuality,
        maxWidth: defaultMaxWidth,
        maxHeight: defaultMaxHeight,
        maxSizeKB: defaultMaxSizeKB
      });
    } catch (error) {
      onError?.(error);
    }
  };

  /**
   * 处理文件选择（UI模式）
   */
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 30) {
      setMessage("一次最多只能上传 30 张图片");
      return;
    }
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    setMessage("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 30) {
      setMessage("一次最多只能上传 30 张图片");
      return;
    }
    setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
    setMessage("");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDelete = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  /**
   * 开始压缩（UI模式）
   */
  const handleCompress = async () => {
    if (files.length === 0) {
      setMessage("请先选择图片");
      return;
    }

    setMessage("正在压缩图片...");
    setIsCompressing(true);

    try {
      const newCompressedResults = [];
      for (const file of files) {
        if (file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png") {
          // UI 模式完全按照界面上用户设置的滑块 quality 进行
          const result = await compressImage(file, quality, maxWidth, maxHeight, maxSizeKB);
          
          newCompressedResults.push({
            name: file.name,
            url: URL.createObjectURL(result.blob),
            originalSize: result.originalSize,
            compressedSize: result.compressedSize,
            width: result.width,
            height: result.height,
            originalWidth: result.originalWidth,
            originalHeight: result.originalHeight,
            blob: result.blob,
            file: result.file
          });
          
          onCompress?.(result.file, file, {
            quality,
            maxWidth,
            maxHeight,
            maxSizeKB
          });
        } else {
          setMessage("只支持 JPG/PNG 格式的图片");
          return;
        }
      }

      setCompressedFiles(prev => [...prev, ...newCompressedResults]);
      setMessage("压缩完成");
      setFiles([]);
    } catch (error) {
      setMessage(error.message);
      onError?.(error);
    } finally {
      setIsCompressing(false);
    }
  };

  const handlePreview = (url, size) => {
    setPreviewImage(url);
    setOriginalSize(size);
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
    setOriginalSize(0);
  };

  const handleReset = () => {
    setFiles([]);
    setCompressedFiles([]);
    setMessage("");
    setQuality(compressionRatio || 80);
    setMaxWidth(defaultMaxWidth);
    setMaxHeight(defaultMaxHeight);
    setMaxSizeKB(defaultMaxSizeKB);
  };

  // 静默模式：不显示UI，直接压缩
  if (!showUI) {
    // 随机 ID 防止页面中多个静默组件冲突
    const inputId = `silent-image-input-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className={styles.silentCompressor}>
        <input
          type="file"
          accept={accept}
          onChange={handleFileSelectSilent}
          style={{ display: 'none' }}
          id={inputId}
        />
        {children ? (
          <div onClick={() => document.getElementById(inputId).click()}>
            {children}
          </div>
        ) : (
          <button 
            className={styles.silentBtn}
            onClick={() => document.getElementById(inputId).click()}
          >
            {triggerText}
          </button>
        )}
      </div>
    );
  }

  // UI 模式：显示完整压缩界面
  return (
    <div className={styles.container}>
      {/* 顶部区域 */}
      <div className={styles.topSection}>
        <div
          className={styles.uploadArea}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => document.querySelector(`.${styles.fileInput}`).click()}
        >
          {files.length === 0 ? (
            <p>点击或拖放上传图片</p>
          ) : (
            <p>已选择 {files.length} 张图片，点击继续添加</p>
          )}
          <input
            type="file"
            className={styles.fileInput}
            multiple
            accept={accept}
            onChange={handleFileChange}
          />
        </div>

        {files.length > 0 && (
          <div className={styles.previewUploadContainer}>
            {files.map((file, index) => (
              <div key={index} className={styles.previewUploadItem}>
                <img
                  src={URL.createObjectURL(file)}
                  alt={`预览 ${index + 1}`}
                  onClick={() => handlePreview(URL.createObjectURL(file), file.size)}
                />
                <button
                  className={styles.deleteBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(index);
                  }}
                >
                  删除
                </button>
              </div>
            ))}
          </div>
        )}

        {files.length > 0 && (
          <div className={`${styles.options} ${isMobile ? styles.mobile : ''}`}>
            <div className={styles.option}>
              <label>压缩质量: {quality}%</label>
              <input
                type="range"
                min="10"
                max="100"
                value={quality}
                onChange={(e) => setQuality(parseInt(e.target.value, 10))}
              />
            </div>
            <div className={styles.option}>
              <label>最大宽度: {maxWidth}px</label>
              <input
                type="range"
                min="100"
                max="2000"
                step="50"
                value={maxWidth}
                onChange={(e) => setMaxWidth(parseInt(e.target.value, 10))}
              />
            </div>
            <div className={styles.option}>
              <label>最大高度: {maxHeight}px</label>
              <input
                type="range"
                min="100"
                max="2000"
                step="50"
                value={maxHeight}
                onChange={(e) => setMaxHeight(parseInt(e.target.value, 10))}
              />
            </div>
            <div className={styles.option}>
              <label>目标大小: {maxSizeKB}KB</label>
              <input
                type="range"
                min="50"
                max="500"
                step="10"
                value={maxSizeKB}
                onChange={(e) => setMaxSizeKB(parseInt(e.target.value, 10))}
              />
            </div>
          </div>
        )}

        {message && <p className={styles.message}>{message}</p>}
      </div>

      {/* 压缩结果预览 */}
      <div className={styles.middleSection}>
        {compressedFiles.length > 0 && (
          <div className={styles.previewContainer}>
            {compressedFiles.map((file, index) => (
              <div key={index} className={styles.previewItem}>
                <img
                  src={file.url}
                  alt={`压缩图片 ${index + 1}`}
                  onClick={() => handlePreview(file.url, file.originalSize)}
                />
                <div className={styles.previewInfo}>
                  <p>原始: {file.originalWidth} × {file.originalHeight}</p>
                  <p>压缩: {file.width} × {file.height}</p>
                  <p>大小: {(file.originalSize / 1024).toFixed(2)}KB → {(file.compressedSize / 1024).toFixed(2)}KB</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 操作按钮 */}
      <div className={styles.actionButtons}>
        {files.length > 0 && (
          <button className={styles.compressBtn} onClick={handleCompress} disabled={isCompressing}>
            {isCompressing ? '压缩中...' : '开始压缩图片'}
          </button>
        )}

        {compressedFiles.length > 0 && (
          <button className={styles.resetBtn} onClick={handleReset}>
            重新上传
          </button>
        )}
      </div>

      {/* 预览弹窗 */}
      {previewImage && (
        <div className={styles.previewModal}>
          <div className={styles.previewModalContent}>
            <span className={styles.close} onClick={handleClosePreview}>&times;</span>
            <img src={previewImage} alt="大图预览" className={styles.previewLarge} />
            <p>原始大小: {(originalSize / 1024).toFixed(2)} KB</p>
          </div>
        </div>
      )}
    </div>
  );
};

ImageCompressor.propTypes = {
  onCompress: PropTypes.func.isRequired,
  onError: PropTypes.func,
  showUI: PropTypes.bool,
  compressionRatio: PropTypes.number,
  maxWidth: PropTypes.number,
  maxHeight: PropTypes.number,
  maxSizeKB: PropTypes.number,
  accept: PropTypes.string,
  children: PropTypes.node,
  triggerText: PropTypes.string
};

export default ImageCompressor;